import { useState, useEffect } from "react";
import {
  Users,
  DollarSign,
  TrendingUp,
  MessageSquare,
  AlertTriangle,
  Activity,
  ArrowUpRight,
  RefreshCw,
  Target,
  Zap,
  UserPlus,
  UserMinus,
} from "lucide-react";
import { useMasterData } from "../hooks/useMasterData";
import { DemoBadge } from "../components/DemoBadge";
import { supabase } from "../providers/AuthProvider";
import { MetricCard } from "../components/dashboard/MetricCard";
import { PerformanceChart } from "../components/dashboard/PerformanceChart";

interface DashboardMetrics {
  // Financial
  mrr: number;
  arr: number;
  mrrGrowth: number;
  arpu: number;
  ltv: number;
  cac: number;

  // Customers
  totalClients: number;
  activeClients: number;
  newClientsThisMonth: number;
  churnedClients: number;
  churnRate: number;
  retentionRate: number;

  // Operational
  totalMessages: number;
  aiResponses: number;
  activeAlerts: number;
  avgResponseTime: number;

  // Support
  openTickets: number;
  avgTicketResolution: number;
}

export interface RevenueDataPoint {
  month?: string;
  date?: string;
  revenue?: number;
  mrr?: number;
  clients?: number;
  [key: string]: string | number | undefined;
}

export interface PlanDistributionData {
  name: string;
  value: number;
}

export interface CustomerGrowthData {
  month: string;
  clients: number;
  new: number;
  churned: number;
}

export interface UsageChartData {
  month: string;
  messages: number;
  ai: number;
}

const COLORS = ["#ef4444", "#f97316", "#eab308", "#22c55e", "#3b82f6"];

export function Dashboard() {
  const { isDemoMode, demoLabel, dashboardMetrics } = useMasterData();

  const [metrics, setMetrics] = useState<DashboardMetrics>({
    // Financial
    mrr: 0,
    arr: 0,
    mrrGrowth: 0,
    arpu: 0,
    ltv: 0,
    cac: 0,
    // Customers
    totalClients: 0,
    activeClients: 0,
    newClientsThisMonth: 0,
    churnedClients: 0,
    churnRate: 0,
    retentionRate: 0,
    // Operational
    totalMessages: 0,
    aiResponses: 0,
    activeAlerts: 0,
    avgResponseTime: 0,
    // Support
    openTickets: 0,
    avgTicketResolution: 0,
  });
  const [loading, setLoading] = useState(true);
  const [revenueData, setRevenueData] = useState<RevenueDataPoint[]>([]);
  const [planDistribution, setPlanDistribution] = useState<PlanDistributionData[]>([]);
  const [customerGrowthData, setCustomerGrowthData] = useState<CustomerGrowthData[]>([]);
  const [usageData, setUsageData] = useState<UsageChartData[]>([]);

  const calculateLTV = (arpu: number, churnRate: number): number => {
    if (churnRate === 0) return arpu * 12;
    return arpu / (churnRate / 100);
  };

  const fetchAIStats = async () => {
    try {
      const { data, error } = await supabase
        .from("client_usage")
        .select("ai_responses_used");
      if (error) throw error;
      const total =
        data?.reduce(
          (sum, d) => sum + (Number(d?.ai_responses_used) || 0),
          0,
        ) ?? 0;
      setMetrics((prev) => ({ ...prev, aiResponses: total }));
    } catch (err) {
      console.error("Erro ao buscar AI stats:", err);
    }
  };

  useEffect(() => {
    if (dashboardMetrics) {
      const mrr = dashboardMetrics.mrr || 0;
      const activeClients = dashboardMetrics.activeClients || 0;
      const arpu = mrr / (activeClients || 1);
      const churnRate = dashboardMetrics.churnRate || 0;

      setMetrics({
        mrr,
        arr: mrr * 12,
        mrrGrowth: dashboardMetrics.mrrGrowth || 0,
        arpu,
        ltv: calculateLTV(arpu, churnRate),
        cac: 0,
        totalClients: dashboardMetrics.totalClients || 0,
        activeClients,
        newClientsThisMonth: dashboardMetrics.newClientsMonth || 0,
        churnedClients: Math.round(
          (dashboardMetrics.totalClients || 0) * (churnRate / 100),
        ),
        churnRate,
        retentionRate: 100 - churnRate,
        totalMessages: dashboardMetrics.totalMessages || 0,
        aiResponses: 0,
        activeAlerts: dashboardMetrics.activeAlerts || 0,
        avgResponseTime: 0,
        openTickets: 0,
        avgTicketResolution: 0,
      });
      fetchAIStats();
      setRevenueData(
        Array.isArray(dashboardMetrics.revenueData)
          ? dashboardMetrics.revenueData
          : [],
      );
      setPlanDistribution(
        Array.isArray(dashboardMetrics.planDistribution)
          ? dashboardMetrics.planDistribution
          : [],
      );

      // Generate customer growth data
      if (
        dashboardMetrics.revenueData &&
 
        Array.isArray(dashboardMetrics.revenueData) &&
        dashboardMetrics.revenueData.length > 0
      ) {
        setCustomerGrowthData(
          dashboardMetrics.revenueData.map((r: RevenueDataPoint) => ({
            month: r.month || r.date || "N/A",
            clients: r.clients || 0,
            new: Math.floor((r.clients || 0) * 0.1),
            churned: Math.floor((r.clients || 0) * 0.02),
          })),
        );
      } else {
        setCustomerGrowthData([]);
      }

      if (
 
        dashboardMetrics.revenueData &&
        Array.isArray(dashboardMetrics.revenueData) &&
        dashboardMetrics.revenueData.length > 0
      ) {
        setUsageData(
          dashboardMetrics.revenueData.map((r: RevenueDataPoint) => ({
            month: r.month || r.date || "N/A",
            messages: Math.floor((r.revenue || r.mrr || 0) * 10),
            ai: Math.floor((r.revenue || r.mrr || 0) * 3),
          })),
        );
      } else {
        setUsageData([]);
 
      }

      setLoading(false);
    } else if (!isDemoMode) {
      fetchDashboardData();
    }
// eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardMetrics, isDemoMode]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Buscar dados reais via RPC functions
      const { data: metricsData, error: metricsError } = await supabase.rpc(
        "get_master_dashboard_metrics",
      );

      if (metricsError) throw metricsError;

      const { data: revenueData, error: revenueError } = await supabase.rpc(
        "get_master_revenue_data",
      );

      if (revenueError) throw revenueError;

      const { data: planData, error: planError } = await supabase.rpc(
        "get_master_plan_distribution",
      );

      if (planError) throw planError;

      // Atualizar métricas
      const mrr = metricsData?.mrr || 0;
      const activeClients = metricsData?.activeClients || 0;
      const arpu = mrr / (activeClients || 1);
      const churnRate = metricsData?.churnRate || 0;

      setMetrics({
        mrr,
        arr: mrr * 12,
        mrrGrowth: metricsData?.mrrGrowth || 0,
        arpu,
        ltv: calculateLTV(arpu, churnRate),
        cac: 0,
        totalClients: metricsData?.totalClients || 0,
        activeClients,
        newClientsThisMonth: metricsData?.newClientsMonth || 0,
        churnedClients: Math.round(
          (metricsData?.totalClients || 0) * (churnRate / 100),
        ),
        churnRate,
        retentionRate: 100 - churnRate,
        totalMessages: metricsData?.totalMessages || 0,
        aiResponses: 0,
        activeAlerts: metricsData?.activeAlerts || 0,
        avgResponseTime: 0,
        openTickets: 0,
        avgTicketResolution: 0,
      });

      setRevenueData(Array.isArray(revenueData) ? revenueData : []);
      setPlanDistribution(Array.isArray(planData) ? planData : []);

      const { data: usageRows } = await supabase
        .from("client_usage")
        .select("ai_responses_used");
      const totalAI =
        usageRows?.reduce(
          (sum, d) => sum + (Number(d?.ai_responses_used) || 0),
          0,
        ) ?? 0;
      setMetrics((prev) => ({ ...prev, aiResponses: totalAI }));

      if (
        !revenueData ||
 
        !Array.isArray(revenueData) ||
        revenueData.length === 0
      ) {
        setCustomerGrowthData([]);
        setUsageData([]);
      } else {
        setCustomerGrowthData(
 
          revenueData.map((r: RevenueDataPoint) => ({
            month: r.month || r.date || "N/A",
            clients: r.clients || 0,
            new: Math.floor((r.clients || 0) * 0.1),
            churned: Math.floor((r.clients || 0) * 0.02),
          })),
        );
        setUsageData(
          revenueData.map((r: RevenueDataPoint) => ({
            month: r.month || r.date || "N/A",
            messages: Math.floor((r.revenue || r.mrr || 0) * 10),
            ai: Math.floor((r.revenue || r.mrr || 0) * 3),
          })),
        );
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      // Em caso de erro, mostrar dados vazios (não mocks)
      setMetrics({
        totalClients: 0,
        activeClients: 0,
        mrr: 0,
        arr: 0,
        mrrGrowth: 0,
        arpu: 0,
        ltv: 0,
        cac: 0,
        newClientsThisMonth: 0,
        churnedClients: 0,
        churnRate: 0,
        retentionRate: 0,
        totalMessages: 0,
        aiResponses: 0,
        activeAlerts: 0,
        avgResponseTime: 0,
        openTickets: 0,
        avgTicketResolution: 0,
      });
      setRevenueData([]);
      setPlanDistribution([]);
      setCustomerGrowthData([]);
      setUsageData([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <>
      {isDemoMode && <DemoBadge label={demoLabel} />}
      <div className="space-y-8 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">
              Dashboard Master
            </h1>
            <p className="text-slate-400">
              Visão geral completa do sistema e performance
            </p>
          </div>
          <button
            onClick={fetchDashboardData}
            className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-slate-300 hover:bg-white/10 transition-all"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Atualizar
          </button>
        </div>

        {/* Financial Metrics */}
        <div>
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-400" />
            Métricas Financeiras
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <MetricCard
              title="MRR"
              value={formatCurrency(metrics.mrr)}
              change={metrics.mrrGrowth}
              icon={<DollarSign className="w-6 h-6" />}
              gradient
              color="red"
              subtitle="Receita Recorrente Mensal"
            />
            <MetricCard
              title="ARR"
              value={formatCurrency(metrics.arr)}
              icon={<TrendingUp className="w-6 h-6" />}
              color="emerald"
              subtitle="Receita Recorrente Anual"
            />
            <MetricCard
              title="ARPU"
              value={formatCurrency(metrics.arpu)}
              icon={<Target className="w-6 h-6" />}
              color="blue"
              subtitle="Receita Média por Usuário"
            />
            <MetricCard
              title="LTV"
              value={formatCurrency(metrics.ltv)}
              icon={<Zap className="w-6 h-6" />}
              color="purple"
              subtitle="Lifetime Value"
            />
            <div className="relative group bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg bg-amber-500/20">
                  <Target className="w-6 h-6 text-amber-400" />
                </div>
                <span className="text-sm text-slate-400">CAC</span>
              </div>
              <p className="text-2xl font-bold text-white">R$ 0</p>
              <p className="text-sm text-slate-500 mt-1">Custo de Aquisição</p>
              <div className="absolute hidden group-hover:block z-10 bottom-full left-0 mb-1 px-2 py-1.5 bg-slate-800 text-xs text-slate-300 rounded shadow-lg whitespace-nowrap">
                Configure custos em Configurações
              </div>
            </div>
          </div>
        </div>

        {/* Customer Metrics */}
        <div>
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-400" />
            Métricas de Clientes
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <MetricCard
              title="Clientes Ativos"
              value={`${metrics.activeClients} / ${metrics.totalClients}`}
              change={
                metrics.newClientsThisMonth > 0
                  ? (metrics.newClientsThisMonth / metrics.totalClients) * 100
                  : undefined
              }
              icon={<Users className="w-6 h-6" />}
              color="emerald"
              subtitle={`+${metrics.newClientsThisMonth} este mês`}
            />
            <MetricCard
              title="Taxa de Churn"
              value={`${metrics.churnRate.toFixed(1)}%`}
              icon={<UserMinus className="w-6 h-6" />}
              color={metrics.churnRate > 5 ? "red" : "amber"}
              subtitle={`${metrics.churnedClients} clientes perdidos`}
            />
            <MetricCard
              title="Retenção"
              value={`${metrics.retentionRate.toFixed(1)}%`}
              icon={<UserPlus className="w-6 h-6" />}
              color="emerald"
              subtitle="Taxa de retenção de clientes"
            />
            <MetricCard
              title="Novos Clientes"
              value={metrics.newClientsThisMonth}
              icon={<ArrowUpRight className="w-6 h-6" />}
              color="cyan"
              subtitle="Este mês"
            />
          </div>
        </div>

        {/* Charts Row 1: Revenue & Customer Growth */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <PerformanceChart
            data={revenueData}
            type="area"
            dataKeys={["revenue"]}
            colors={["#ef4444"]}
            title="Evolução da Receita (MRR)"
            subtitle="Últimos 6 meses"
            formatValue={(v) => formatCurrency(v)}
          />
          <PerformanceChart
            data={customerGrowthData}
            type="line"
            dataKeys={["clients", "new", "churned"]}
            colors={["#3b82f6", "#10b981", "#ef4444"]}
            title="Crescimento de Clientes"
            subtitle="Novos vs Churn"
          />
        </div>

        {/* Charts Row 2: Usage & Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <PerformanceChart
            data={usageData}
            type="bar"
            dataKeys={["messages", "ai"]}
            colors={["#ef4444", "#3b82f6"]}
            title="Uso do Sistema"
            subtitle="Mensagens e Respostas IA"
            height={250}
 
          />
          <div className="lg:col-span-2 bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-6">
              Distribuição de Planos
            </h3>
            <div className="space-y-4">
              {planDistribution && planDistribution.length > 0 ? (
                planDistribution.map((plan, index) => {
                  const total = planDistribution.reduce(
                    (acc: number, p: PlanDistributionData) => acc + (p.value || 0),
                    0,
                  );
                  const percentage =
                    total > 0 ? ((plan.value || 0) / total) * 100 : 0;
                  return (
                    <div key={plan.name || index} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-white capitalize">
                          {plan.name || "N/A"}
                        </span>
                        <span className="text-sm text-slate-400">
                          {plan.value || 0} ({(percentage || 0).toFixed(1)}%)
                        </span>
                      </div>
                      <div className="w-full bg-white/5 rounded-full h-2 overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${percentage}%`,
                            backgroundColor: COLORS[index % COLORS.length],
                          }}
                        />
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-slate-400 text-sm">Nenhum dado disponível</p>
              )}
            </div>
          </div>
        </div>

        {/* Operational Metrics */}
        <div>
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-purple-400" />
            Métricas Operacionais
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <MetricCard
              title="Mensagens Totais"
              value={metrics.totalMessages.toLocaleString("pt-BR")}
              icon={<MessageSquare className="w-6 h-6" />}
              color="blue"
              subtitle="Este mês"
            />
            <MetricCard
              title="Respostas IA"
              value={metrics.aiResponses.toLocaleString("pt-BR")}
              icon={<Zap className="w-6 h-6" />}
              color="purple"
              subtitle="Este mês"
            />
            <MetricCard
              title="Tickets Abertos"
              value={metrics.openTickets}
              icon={<AlertTriangle className="w-6 h-6" />}
              color={metrics.openTickets > 10 ? "red" : "amber"}
              subtitle="Aguardando resolução"
            />
            <MetricCard
              title="Alertas Ativos"
              value={metrics.activeAlerts}
              icon={<AlertTriangle className="w-6 h-6" />}
              color={metrics.activeAlerts > 0 ? "red" : "slate"}
              subtitle="Requerem atenção"
            />
          </div>
        </div>
      </div>
    </>
  );
}
